import { Outlet, Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Instagram, Mail, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { publicApi } from '@/lib/api';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_843f7c3a-3692-4728-b14f-ba431557a9c8/artifacts/qxen4yga_Ray%20Creators%20-%20Logo.png";

const Layout = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const location = useLocation();

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/gallery', label: 'Gallery' },
    { path: '/about', label: 'About' },
    { path: '/contact', label: 'Contact' },
  ];

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const data = await publicApi.getSettings();
      setSettings(data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border">
        <div className="container mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2" data-testid="header-logo">
              <img 
                src={LOGO_URL} 
                alt="Ray Creators" 
                className="h-10 w-10 object-contain"
              />
              <div className="brand-title text-xl">
                <span className="ray">RAY</span>
                <span className="creators">Creators</span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`nav-link ${isActive(link.path) ? 'active' : ''}`}
                  data-testid={`nav-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X /> : <Menu />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.nav
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden border-t border-border"
              data-testid="mobile-nav"
            >
              <div className="container mx-auto px-6 py-4 flex flex-col gap-4">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`nav-link ${isActive(link.path) ? 'active' : ''}`}
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-foreground text-background py-12" data-testid="footer">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Brand */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src={LOGO_URL} 
                  alt="Ray Creators" 
                  className="h-10 w-10 object-contain"
                />
                <div className="text-xl">
                  <span className="font-serif text-white uppercase tracking-wider">Ray</span>
                  <span className="font-script text-primary text-2xl ml-1">Creators</span>
                </div>
              </div>
              <p className="text-muted-foreground text-sm">
                Crafting visual stories that captivate and inspire. 
                From branding to packaging, we bring your vision to life.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-serif text-lg mb-4">Quick Links</h4>
              <div className="flex flex-col gap-2">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className="text-muted-foreground hover:text-primary transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-serif text-lg mb-4">Get in Touch</h4>
              <div className="flex flex-col gap-3 text-sm text-muted-foreground">
                <a 
                  href={`mailto:${settings?.email || 'hello@raycreators.com'}`} 
                  className="flex items-center gap-2 hover:text-primary transition-colors"
                >
                  <Mail size={16} />
                  {settings?.email || 'hello@raycreators.com'}
                </a>
                <a 
                  href={`tel:${settings?.phone?.replace(/[^\d+]/g, '') || '+1234567890'}`} 
                  className="flex items-center gap-2 hover:text-primary transition-colors"
                >
                  <Phone size={16} />
                  {settings?.phone || '+1 (234) 567-890'}
                </a>
                <a 
                  href={settings?.instagram_url || 'https://instagram.com'} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="flex items-center gap-2 hover:text-primary transition-colors"
                >
                  <Instagram size={16} />
                  {settings?.instagram || '@raycreators'}
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-muted-foreground/20 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-muted-foreground">
            <p>&copy; {new Date().getFullYear()} Ray Creators. All rights reserved.</p>
            <p>
              Made with <span className="text-red-500">♥</span> by{' '}
              <a 
                href="https://ayrasoftwaretechnologies.in" 
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:text-primary transition-colors"
              >
                Ayra Software Technologies
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
